import "webdatarocks/webdatarocks.css";
import React, { useEffect, useRef, useState } from "react";
import { useDarkModeStore } from "../../../store/useDarkModeStore";
import WebDataRocks from "webdatarocks";
import LoadingRaport from "./LoadingRaport";
import useAssets from "../../../store/ReactQueries/assets/asset/useAsset";
import { DataSelectType } from "../../../models/DataSelectType";
import { AssetInterface } from "../../../models/AssetModel/Asset";
import assetService from "../../../services/assetService/assetService";
import FilterAssetComponent from "../Assets/Components/Filter/FilterAssetComponent";
import Button from "@mui/joy/Button";
import Typography from "@mui/joy/Typography";

const PAGE_SIZE = 100;
const rowsName = [
  "Barcode",
  "Title",
  "Description",
  "Asset Category",
  "Asset Type",
  "Location",
  "Business Unit",
  "Asset Department",
  "Asset Status",
  "Created Date",
  "Updated Date",
];

const AssetsRaport = () => {
  const pivotRef = useRef<HTMLDivElement>(null);
  const pivotInstanceRef = useRef<InstanceType<typeof WebDataRocks> | null>(null);
  const pagedDataRef = useRef<object[]>([]);

  const [page, setPage] = useState<number>(1);
  const [filterData, setFilterData] = useState<DataSelectType[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const { darkMode } = useDarkModeStore();

  const { data: assets, isLoading, error: assetErrors } = useAssets(page, PAGE_SIZE, filterData);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setPage(1);
  }, [filterData]);

  useEffect(() => {
    if (!pivotRef.current || !assets || isLoading || assetErrors) return;

    setLoading(true);

    const mapAsset = (asset: AssetInterface) => ({
      Barcode: `${asset.barcode.toString()}`,
      Title: asset.title,
      Description: asset.description,
      "Asset Category": asset.assetCategory?.title,
      "Asset Type": asset.assetType?.title,
      Location: asset.location?.title,
      "Asset Department": asset.assetDepartment?.title,
      "Asset Status": asset.assetStatus?.title || "",
      "Business Unit": asset.customer?.title || "",
      "Created Date": asset.createdDate && new Date(asset.createdDate).toLocaleDateString(),
      "Updated Date": asset.updatedDate && new Date(asset.updatedDate).toLocaleDateString(),
    });

    const assetsData = assets.map(mapAsset);
    pagedDataRef.current = assetsData;

    if (!pivotInstanceRef.current) {
      pivotInstanceRef.current = new WebDataRocks({
        container: pivotRef.current,
        toolbar: false,
        height: "100%",
        width: "100%",
        report: {
          dataSource: { data: assetsData },
          options: {
            grid: {
              type: "flat",
              showGrandTotals: "off",
              showTotals: "off",
              showFilter: false,
            },
          },
          slice: {
            rows: rowsName.map((uniqueName) => ({ uniqueName })),
            columns: [],
            measures: [],
          },
        },
        reportcomplete: () => {
          setTimeout(() => setLoading(false), 500);
        },
      });
    } else {
      pivotInstanceRef.current.updateData({ data: assetsData });
      setLoading(false);
    }
  }, [assets, isLoading, assetErrors, darkMode]);

  const handleExportAll = async () => {
    if (!pivotInstanceRef.current) return;
    setIsExporting(true);
    try {
      const allAssets = await assetService.GetAll(0, 0, filterData);
      const allData = allAssets.map((asset) => ({
        Barcode: `${asset.barcode.toString()}`,
        Title: asset.title,
        Description: asset.description,
        "Asset Category": asset.assetCategory?.title,
        "Asset Type": asset.assetType?.title,
        Location: asset.location?.title,
        "Asset Department": asset.assetDepartment?.title,
        "Asset Status": asset.assetStatus?.title || "",
        "Created Date": asset.createdDate && new Date(asset.createdDate).toLocaleDateString(),
        "Updated Date": asset.updatedDate && new Date(asset.updatedDate).toLocaleDateString(),
      }));

      pivotInstanceRef.current.updateData({ data: allData });

      setTimeout(() => {
        pivotInstanceRef.current?.exportTo("excel", { filename: "assets-report" }, () => {
          pivotInstanceRef.current?.updateData({ data: pagedDataRef.current });
          setIsExporting(false);
        });
      }, 200);
    } catch {
      setIsExporting(false);
    }
  };

  return (
    <div className="mb-4" style={{ height: "83vh", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "4px 0" }}>
        <Button size="sm" variant="solid" loading={isExporting} onClick={handleExportAll}>
          Export All
        </Button>
        <Button size="sm" variant="outlined" onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "row", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
          {loading && <LoadingRaport />}

          <div ref={pivotRef} style={{ height: "100%", width: "100%" }} />
        </div>
        {showFilters && (
          <FilterAssetComponent
            showFilters={showFilters}
            page={page}
            pageSize={PAGE_SIZE}
            filterData={filterData}
            setPage={setPage}
            setFilterData={setFilterData}
          />
        )}
      </div>

      {/* Pagination bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0" }}>
        <Button size="sm" variant="outlined" disabled={page === 1 || isExporting} onClick={() => setPage((p) => p - 1)}>
          Previous
        </Button>
        <Typography level="body-sm">Page {page}</Typography>
        <Button
          size="sm"
          variant="outlined"
          disabled={!assets || assets.length < PAGE_SIZE || isExporting}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default AssetsRaport;
